import { __analyticsContentViewFix__ } from '@/Telemetry/infobox';
import { __useDfMessenger__ } from '@/Utility/initApp/dialogflow-cx-messenger/useDfMessenger';
import { ZnglyVideosFrontendPaginationFragment, ZnglyVideosFrontendQuery, type ZnglyVideos_Frontend_Pagination_Fragment$key, type ZnglyVideos_Frontend_Query, type ZnglyVideos_Frontend_Query$variables } from '@/graphql/queries/ZnglyVideo.query';
import { usePaginationFragment } from '@/graphql/relay.utils';
import { type AppStore, useAppStore } from '@/inertia/AppStore';
import MainContainer from '@/inertia/Shared/MainContainer';
import Page from '@/inertia/Shared/Page';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { type PreloadedQuery, usePreloadedQuery } from 'react-relay';
import type { DeepPick } from 'ts-essentials';
import { z } from 'zod';
type Props = {
  isFallback?: boolean;
} & Partial<ReturnType<typeof usePaginationFragment<ZnglyVideos_Frontend_Query, ZnglyVideos_Frontend_Pagination_Fragment$key>>>;
const Home = (props: Props) => {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const pageType = useAppStore(store => store.pageType);
  __analyticsContentViewFix__();
  __useDfMessenger__({
    pageType: pageType
  });
  useEffect(() => {
    if (!props?.data?.znglyVideos) return;
    const nodes = props?.data?.znglyVideos?.edges?.map(edge => edge?.node) as AppStore['nodes'] ?? [];
    let activeNodeIdx = 0;
    if (search?.c) {
      const foundIdx = nodes.findIndex(node => node?.id === search?.c);
      if (foundIdx > -1) activeNodeIdx = foundIdx;
    }
    useAppStore.setState({
      nodes: [...nodes],
      catalogNodes: [...nodes],
      playerNode: !!nodes?.[activeNodeIdx] ? {
        ...nodes?.[activeNodeIdx]
      } : null
    });
    if (!search?.c && nodes?.[activeNodeIdx] || search?.c !== nodes?.[activeNodeIdx]?.id) {
      navigate({
        from: '/',
        search: {
          ...search,
          ...(nodes?.[activeNodeIdx]?.id && {
            c: nodes?.[activeNodeIdx]?.id
          })
        }
      });
    }
  }, [props?.data?.znglyVideos]);

  // Set the first node as the player node if no node is selected
  useEffect(() => {
    if (!search.c) {
      const nodes = useAppStore.getState().nodes;
      if (nodes.length > 0) {
        navigate({
          from: '/',
          search: {
            ...search,
            c: nodes[0].id
          }
        });
        useAppStore.setState({
          playerNode: nodes[0]
        });
      }
    }
  }, [search?.c]);
  useEffect(() => {
    return () => {
      useAppStore.getState().setPlayerNode(null);
    };
  }, []);
  return <Page>
      <MainContainer isFallback={props?.isFallback} />
    </Page>;
};
const searchSchema = z.object({
  s: z.string().optional().nullable().catch(null),
  c: z.string().optional().nullable().catch(null),
  categories: z.array(z.string()).optional().nullable().catch(null)
});
type SearchSchema = z.infer<typeof searchSchema>;
type LoaderDeps = DeepPick<SearchSchema, {
  s: never;
  categories: never;
}>;
import { Route } from "/home/runner/work/zngly-corporate/zngly-corporate/wp-content/themes/zngly-corporate/frontend/src/routes/index.tsx";
const SplitComponent = () => {
  const loaderData = Route.useLoaderData() as PreloadedQuery<ZnglyVideos_Frontend_Query, ZnglyVideos_Frontend_Query$variables>;
  const queryRef = usePreloadedQuery<ZnglyVideos_Frontend_Query>(ZnglyVideosFrontendQuery, loaderData);
  const paginationData = usePaginationFragment<ZnglyVideos_Frontend_Query, ZnglyVideos_Frontend_Pagination_Fragment$key>(ZnglyVideosFrontendPaginationFragment, queryRef);
  const search = Route.useSearch({
    select(state) {
      return {
        s: state?.s,
        categories: state?.categories
      };
    }
  });

  // search param updates
  useEffect(() => {
    if (!!search?.s) {
      useAppStore.setState({
        pageType: 'search'
      });
    } else if (!!search?.categories?.length) {
      useAppStore.setState({
        pageType: 'category'
      });
    } else {
      useAppStore.setState({
        pageType: 'front_page'
      });
    }
  }, [search?.categories, search?.s]);

  // load more function
  const loadMoreFn = useCallback(() => {
    if (!paginationData?.loadNext || paginationData?.isLoadingNext || !paginationData?.hasNext) return;
    paginationData?.loadNext?.(24);
  }, [paginationData?.loadNext, paginationData?.isLoadingNext, paginationData?.hasNext]);
  useEffect(() => {
    useAppStore.setState({
      catalogLoadMore: loadMoreFn
    });
  }, [loadMoreFn]);

  // loading effect
  useEffect(() => {
    useAppStore.setState({
      isCatalogLoadingMore: paginationData?.isLoadingNext
    });
  }, [paginationData?.isLoadingNext]);
  return <Home {...paginationData} />;
};
export { SplitComponent as component };